import React, { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Sparkles, Loader2, Play, Pause, Download, Share2, Check, AlertCircle, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveAudio = (u) => !u ? "" : (u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

const VIBES = [
  { id: "warm",  label: "Warm",  desc: "Inspirational" },
  { id: "hype",  label: "Hype",  desc: "High-energy" },
  { id: "chill", label: "Chill", desc: "Conversational" },
  { id: "bold",  label: "Bold",  desc: "Rebellious" },
];

const SHARE_URL = `${typeof window !== "undefined" ? window.location.origin : "https://fliboe.com"}/free-sample`;

function shareForm() {
  const payload = {
    title: "Free 30-second AI radio ad from Fliboe FM",
    text: "Tell us about your business in 6 questions — we make you a free AI-voiced radio ad in 30 seconds.",
    url: SHARE_URL,
  };
  if (navigator.share) { navigator.share(payload).catch(() => {}); }
  else { navigator.clipboard.writeText(`${payload.text} ${payload.url}`); }
}

export default function FreeSample() {
  const { id } = useParams();
  if (id) return <FreeSampleResult id={id} />;
  return <FreeSampleForm />;
}

function FreeSampleForm() {
  const [a, setA] = useState({
    business_name: "", contact_email: "", audience: "", why: "", tagline: "", cta: "", vibe: "warm",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [shared, setShared] = useState(false);

  const submit = async () => {
    setErr("");
    for (const k of ["business_name", "contact_email", "audience", "why", "tagline", "cta"]) {
      if (!a[k]?.trim()) { setErr("Fill every field — short sentences are fine."); return; }
    }
    if (!a.contact_email.includes("@")) { setErr("Enter a valid email."); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/produce/free-sample", {
        ...a,
        referrer: document.referrer || "",
      });
      window.location.href = `/free-sample/${data.sample_id}`;
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not start your sample. Try again.");
      setBusy(false);
    }
  };

  const onShare = async () => {
    shareForm();
    setShared(true);
    setTimeout(() => setShared(false), 1500);
  };

  return (
    <main className="max-w-2xl mx-auto px-5 py-14" data-testid="free-sample-form-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-500 flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> Free sample · no card needed
          </div>
          <h1 className="fl-display text-5xl font-extrabold tracking-tighter mt-2">
            We'll make you a <span className="text-emerald-500">30-second radio ad.</span>
          </h1>
          <p className="text-slate-400 mt-3">
            Answer 6 quick questions. AI writes + voices your ad. ~30 seconds later you get the MP3.
            <strong className="text-white"> Yours forever. Free.</strong>
          </p>
        </div>
        <button onClick={onShare} className="fl-btn ghost shrink-0" data-testid="free-sample-share">
          {shared ? <Check className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4 text-emerald-500" />}
          {shared ? "Link copied" : "Share this"}
        </button>
      </div>

      {/* Form */}
      <div className="space-y-4 mt-10">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business / show name" value={a.business_name} onChange={(v) => setA({ ...a, business_name: v })} placeholder="Joe's Pizza" testid="fs-biz" />
          <Field label="Your email (so we can send you the MP3)" type="email" value={a.contact_email} onChange={(v) => setA({ ...a, contact_email: v })} placeholder="you@business.com" testid="fs-email" />
        </div>
        <Field label="Who's your customer? (5 words)" value={a.audience} onChange={(v) => setA({ ...a, audience: v })} placeholder="hungry locals · indie artists · busy moms" testid="fs-audience" />
        <Field label="What's the one thing you do better than anyone else?" value={a.why} onChange={(v) => setA({ ...a, why: v })} placeholder="we make every pie fresh — never frozen — with my grandma's sauce recipe" testid="fs-why" multiline />
        <Field label="Your signature line / tagline" value={a.tagline} onChange={(v) => setA({ ...a, tagline: v })} placeholder="Come hungry. Leave happy." testid="fs-tagline" />
        <Field label="What should listeners DO after they hear it?" value={a.cta} onChange={(v) => setA({ ...a, cta: v })} placeholder="Walk in or call 555-1234" testid="fs-cta" />
        <div>
          <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">Voice vibe</label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2">
            {VIBES.map((v) => (
              <button
                key={v.id}
                onClick={() => setA({ ...a, vibe: v.id })}
                className={`fl-card p-3 text-left transition ${a.vibe === v.id ? "ring-2 ring-emerald-500 bg-emerald-500/5" : "hover:bg-white/5"}`}
                data-testid={`fs-vibe-${v.id}`}
              >
                <div className="text-white text-sm font-semibold">{v.label}</div>
                <div className="text-[10px] text-slate-400 mt-0.5">{v.desc}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {err && (
        <div className="mt-4 flex items-start gap-2 text-sm text-red-400" data-testid="fs-error">
          <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /> {err}
        </div>
      )}
      <button onClick={submit} disabled={busy} className="fl-btn mt-6 w-full sm:w-auto" data-testid="fs-submit">
        <Sparkles className="w-4 h-4" />
        {busy ? "Sending to studio…" : "Make my free sample →"}
      </button>
      <p className="text-xs text-slate-500 mt-3">
        Limit: 1 free sample per email per day. Want unlimited + 3-5 minute versions? <Link to="/produce" className="text-emerald-500 hover:underline">Try the full /produce tool</Link>.
      </p>
    </main>
  );
}

function FreeSampleResult({ id }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const [playing, setPlaying] = useState(false);
  const [shared, setShared] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const { data } = await api.get(`/produce/free-sample/${id}`);
        if (alive) setS(data);
      } catch (e) {
        if (alive) setErr(e?.response?.data?.detail || "Sample not found");
      }
    };
    load();
    const i = setInterval(() => { if (!s || s.status === "queued" || s.status === "generating") load(); else clearInterval(i); }, 3000);
    return () => { alive = false; clearInterval(i); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const onShareResult = () => {
    const url = window.location.href;
    const payload = {
      title: s?.business_name ? `${s.business_name} on Fliboe FM` : "My Fliboe sample",
      text: "Listen to this AI-voiced radio ad I just got for free 🎙️",
      url,
    };
    if (navigator.share) { navigator.share(payload).catch(() => {}); }
    else { navigator.clipboard.writeText(`${payload.text} ${payload.url}`); }
    setShared(true);
    setTimeout(() => setShared(false), 1500);
  };

  const togglePlay = () => {
    const el = audioRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); }
    else { el.play(); setPlaying(true); }
  };

  if (err) return (
    <main className="max-w-xl mx-auto px-5 py-20 text-center" data-testid="fs-result-error">
      <div className="text-red-400">{err}</div>
      <Link to="/free-sample" className="fl-btn mt-6 inline-flex"><Sparkles className="w-4 h-4" /> Make a new one</Link>
    </main>
  );

  if (!s) return (
    <main className="max-w-xl mx-auto px-5 py-20 text-center text-slate-500 fl-mono text-xs uppercase tracking-widest">
      <Loader2 className="w-4 h-4 animate-spin inline mr-2" /> Loading your sample…
    </main>
  );

  if (s.status === "queued" || s.status === "generating") {
    return (
      <main className="max-w-xl mx-auto px-5 py-20" data-testid="fs-result-loading">
        <div className="fl-card p-6 flex items-start gap-3">
          <Loader2 className="w-6 h-6 text-emerald-500 animate-spin shrink-0 mt-1" />
          <div>
            <div className="text-white font-medium">Writing + voicing your sample…</div>
            <div className="text-xs text-slate-400 mt-1">~20-30 seconds. The page refreshes automatically.</div>
          </div>
        </div>
      </main>
    );
  }

  if (s.status === "error") {
    return (
      <main className="max-w-xl mx-auto px-5 py-20" data-testid="fs-result-failed">
        <div className="fl-card p-6 flex items-start gap-3 border-red-500/40">
          <AlertCircle className="w-6 h-6 text-red-400 shrink-0 mt-1" />
          <div>
            <div className="text-white font-medium">Generation failed</div>
            <div className="text-xs text-slate-400 mt-1">{s.error || "Try again in a few seconds."}</div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="max-w-2xl mx-auto px-5 py-14" data-testid="fs-result-ready">
      <div className="fl-mono text-[11px] tracking-[0.3em] uppercase text-emerald-500 flex items-center gap-2">
        <Sparkles className="w-3.5 h-3.5" /> Sample ready
      </div>
      <h1 className="fl-display text-4xl font-extrabold tracking-tighter mt-2">{s.business_name}</h1>
      <div className="text-slate-400 text-sm">{Math.round(s.duration || 0)} seconds · made by AI in real time</div>

      {/* Player + actions */}
      <div className="fl-card p-5 mt-6">
        <div className="flex items-center gap-3 flex-wrap">
          <button onClick={togglePlay} className="fl-btn !p-3" data-testid="fs-play">
            {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
          </button>
          <a
            href={resolveAudio(s.audio_url)}
            download={`fliboe_sample_${s.business_name.replace(/\s+/g, "_")}.mp3`}
            className="fl-btn ghost"
            data-testid="fs-download"
          >
            <Download className="w-4 h-4" /> MP3
          </a>
          <button onClick={onShareResult} className="fl-btn ghost" data-testid="fs-share-result">
            {shared ? <Check className="w-4 h-4 text-emerald-400" /> : <Share2 className="w-4 h-4 text-emerald-500" />}
            {shared ? "Link copied" : "Share this sample"}
          </button>
        </div>
        <audio
          ref={audioRef}
          src={resolveAudio(s.audio_url)}
          onEnded={() => setPlaying(false)}
          preload="metadata"
          className="hidden"
        />
      </div>

      <details className="mt-4">
        <summary className="text-xs fl-mono uppercase tracking-widest text-emerald-500 cursor-pointer">Read the script</summary>
        <pre className="mt-3 fl-card p-4 text-sm text-slate-300 whitespace-pre-wrap leading-relaxed" data-testid="fs-script">{s.script}</pre>
      </details>

      {/* Upsell */}
      <Link to="/produce" className="fl-card p-5 mt-8 block hover:bg-white/5 transition border-emerald-500/40 bg-emerald-500/5" data-testid="fs-upsell">
        <div className="flex items-center justify-between gap-3">
          <div>
            <div className="text-white font-bold text-lg">Want a full 3-min version?</div>
            <div className="text-xs text-slate-400 mt-1">
              $25 for a full 3-min show · $60 to also air it on every Fliboe station for 7 days. Same AI, no human hand.
            </div>
          </div>
          <ArrowRight className="w-5 h-5 text-emerald-500 shrink-0" />
        </div>
      </Link>
    </main>
  );
}

function Field({ label, value, onChange, placeholder, testid, multiline, type = "text" }) {
  return (
    <div>
      <label className="text-xs fl-mono uppercase tracking-widest text-slate-400">{label}</label>
      {multiline ? (
        <textarea value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="fl-input mt-1.5 h-20" data-testid={testid} />
      ) : (
        <input type={type} value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="fl-input mt-1.5" data-testid={testid} />
      )}
    </div>
  );
}
